import React, { useEffect, useRef, useState } from "react";
import ModalContainer from "../ModalContainer.tsx";
import { autocompletePartners } from "../../services/partner.service.ts";
import {
  CollaboratorBossLookup,
  lookupCollaboratorDirectBoss,
} from "../../services/direct-boss.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import BossProfileAvatar from "./BossProfileAvatar.tsx";
import EmployeeInitialsAvatars from "./EmployeeInitialsAvatars.tsx";
import { directBossPageStyles as s } from "./directBossPageStyles.ts";

type PartnerOption = {
  id: number;
  person: {
    firstName: string;
    lastName: string;
    secondLastName?: string;
    curp?: string;
  };
  candidate?: {
    branch?: { name: string };
    vacancy?: { position?: { name: string } };
    shift?: { name: string };
  };
};

type LookupCollaboratorBossModalProps = {
  open: boolean;
  onClose: () => void;
};

const formatPersonName = (person?: {
  firstName?: string;
  lastName?: string;
  secondLastName?: string;
}) => {
  if (!person) return "N/A";
  return toCamelCase(
    `${person.firstName || ""} ${person.lastName || ""} ${
      person.secondLastName || ""
    }`.trim()
  );
};

const LookupCollaboratorBossModal = ({
  open,
  onClose,
}: LookupCollaboratorBossModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPartners, setFilteredPartners] = useState<PartnerOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [result, setResult] = useState<CollaboratorBossLookup | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetState = () => {
    setSearchTerm("");
    setFilteredPartners([]);
    setShowDropdown(false);
    setSearchLoading(false);
    setLookupLoading(false);
    setResult(null);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadPartners = async (term: string) => {
    try {
      setSearchLoading(true);
      const data = await autocompletePartners(term);
      setFilteredPartners(data || []);
    } catch {
      setFilteredPartners([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setResult(null);
    setShowDropdown(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setFilteredPartners([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadPartners(value.trim());
    }, 400);
  };

  const handleLookup = async (partner: PartnerOption) => {
    const label = formatPersonName(partner.person);
    setSearchTerm(label);
    setShowDropdown(false);
    setLookupLoading(true);
    setResult(null);

    try {
      const data = await lookupCollaboratorDirectBoss(partner.id);
      setResult(data);
    } catch (error) {
      console.error("Error looking up direct boss:", error);
      notification(
        "Error",
        "No se pudo consultar el jefe directo del colaborador",
        "danger"
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const employeeMeta = result?.employee?.candidate;

  const teamMembers = React.useMemo(() => {
    if (!result?.bossAssignments?.length) return [];
    const seen = new Map<
      number,
      NonNullable<CollaboratorBossLookup["bossAssignments"][0]["employees"]>[number]
    >();
    for (const assignment of result.bossAssignments) {
      for (const member of assignment.employees || []) {
        seen.set(member.id, member);
      }
    }
    return Array.from(seen.values());
  }, [result]);

  const statusLabelStyle = (color: string): React.CSSProperties => ({
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color,
    marginBottom: 8,
  });

  return (
    <ModalContainer
      open={open}
      closeFn={handleClose}
      title="Encontrar jefe directo de colaborador"
      size="md"
    >
      <div>
        <p className="text-muted mb-3" style={{ fontSize: 14, margin: 0 }}>
          Escribe el nombre del colaborador y selecciónalo de la lista para ver
          quién es su jefe directo.
        </p>

        <label className="form-label fw-semibold mb-2">Colaborador</label>
        <div ref={dropdownRef}>
          <div className="input-group">
            <span className="input-group-text bg-light">
              {searchLoading || lookupLoading ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (
                <i className="fa fa-search opacity-5" />
              )}
            </span>
            <input
              type="text"
              className="form-control bg-light"
              placeholder="Nombre del colaborador..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => {
                setShowDropdown(true);
                if (!searchTerm.trim() && filteredPartners.length === 0) {
                  loadPartners("");
                }
              }}
            />
          </div>

          {showDropdown && searchTerm.trim() && !searchLoading && filteredPartners.length === 0 && (
            <p className="text-muted small mt-2 mb-0">
              No se encontraron colaboradores con ese nombre.
            </p>
          )}

          {showDropdown && filteredPartners.length > 0 && (
            <div
              className="mt-2"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                overflow: "hidden",
                maxHeight: 240,
                overflowY: "auto",
                backgroundColor: "#ffffff",
              }}
            >
              {filteredPartners.map((partner, index) => (
                <button
                  key={partner.id}
                  type="button"
                  className="btn btn-link text-decoration-none text-start w-100"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 0,
                    borderBottom:
                      index < filteredPartners.length - 1
                        ? "1px solid #f1f5f9"
                        : "none",
                    color: "#0f172a",
                  }}
                  onClick={() => handleLookup(partner)}
                  disabled={lookupLoading}
                >
                  <div className="fw-semibold" style={{ fontSize: 14 }}>
                    {formatPersonName(partner.person)}
                  </div>
                  {partner.candidate?.branch?.name && (
                    <div
                      className="text-muted"
                      style={{ fontSize: 12, marginTop: 2, lineHeight: 1.4 }}
                    >
                      {partner.candidate.branch.name}
                      {partner.candidate?.vacancy?.position?.name
                        ? ` · ${partner.candidate.vacancy.position.name}`
                        : ""}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {lookupLoading && (
          <div className="text-center py-4 text-muted">
            <i className="fa fa-spinner fa-spin me-2" />
            Consultando información del colaborador...
          </div>
        )}

        {!lookupLoading && result && (
          <div
            className="mt-4 p-3 rounded"
            style={{
              border: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
            }}
          >
            <div style={s.bossCell} className="mb-3">
              <BossProfileAvatar
                partnerId={result.employee.id}
                curp={result.employee.person?.curp}
                firstName={result.employee.person?.firstName}
                lastName={result.employee.person?.lastName}
                size={48}
              />
              <div>
                <div style={s.bossName}>
                  {formatPersonName(result.employee.person)}
                </div>
                <div style={s.bossRole}>Colaborador</div>
                {employeeMeta?.branch?.name && (
                  <div style={{ ...s.metaValue, marginTop: 4, fontSize: 13 }}>
                    {employeeMeta.branch.name}
                    {employeeMeta.vacancy?.position?.name
                      ? ` · ${employeeMeta.vacancy.position.name}`
                      : ""}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <div style={statusLabelStyle("#64748b")}>
                <i className="fa fa-user me-1" />
                Su jefe directo
              </div>
              {result.hasDirectBoss && result.directBoss?.boss ? (
                <div
                  style={{
                    ...s.bossCell,
                    padding: "12px 14px",
                    borderRadius: 10,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <BossProfileAvatar
                    partnerId={result.directBoss.boss.id}
                    curp={result.directBoss.boss.person?.curp}
                    firstName={result.directBoss.boss.person?.firstName}
                    lastName={result.directBoss.boss.person?.lastName}
                    size={44}
                  />
                  <div>
                    <div style={s.bossName}>
                      {formatPersonName(result.directBoss.boss.person)}
                    </div>
                    <div style={s.bossRole}>Jefe directo asignado</div>
                  </div>
                </div>
              ) : (
                <div
                  className="alert alert-warning mb-0 py-2 px-3"
                  role="alert"
                  style={{ fontSize: 14 }}
                >
                  <i className="fa fa-info-circle me-2" />
                  No tiene jefe directo asignado.
                </div>
              )}
            </div>

            <div>
              <div style={statusLabelStyle("#2563eb")}>
                <i className="fa fa-users me-1" />
                ¿Es jefe directo?
              </div>
              {result.isDirectBoss ? (
                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: 10,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    className="mb-2"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#16a34a",
                    }}
                  >
                    <i className="fa fa-check-circle me-1" />
                    Sí, es jefe directo
                    {result.teamCount > 0 && (
                      <span style={{ fontWeight: 500, color: "#64748b" }}>
                        {" "}
                        · {result.teamCount}{" "}
                        {result.teamCount === 1
                          ? "colaborador a su cargo"
                          : "colaboradores a su cargo"}
                      </span>
                    )}
                  </div>
                  {teamMembers.length > 0 ? (
                    <EmployeeInitialsAvatars employees={teamMembers} />
                  ) : (
                    <span
                      style={{
                        fontSize: 13,
                        color: "#94a3b8",
                        fontStyle: "italic",
                      }}
                    >
                      Sin colaboradores asignados en su equipo
                    </span>
                  )}
                </div>
              ) : (
                <div
                  className="alert alert-secondary mb-0 py-2 px-3"
                  role="alert"
                  style={{ fontSize: 14 }}
                >
                  <i className="fa fa-minus-circle me-2" />
                  No es jefe directo de ningún colaborador.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end mt-4 pt-2">
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cerrar
          </button>
        </div>
      </div>
    </ModalContainer>
  );
};

export default LookupCollaboratorBossModal;
